import { useState } from 'react';
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, X, DollarSign, Users, Zap, Calendar, Pencil, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useUpdateCalendarDay } from '@/hooks/use-calendar';
import { CalendarDay, CalendarData, enrichCalendarWithRules } from '@/api/calendar';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { PricingRule, AvailabilityRule, Event, Property } from '@/types/revenue-manager';
import { useCurrencyFormat } from '@/hooks/use-currency-format';

interface CalendarViewProps {
  property: Property;
  propertyId: string;
  basePrice: number;
  pricingRules?: PricingRule[];
  availabilityRules?: AvailabilityRule[];
  events?: Event[];
}

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

// Helper to check if a rule is active on a given date
const isRuleActiveOnDate = (rule: PricingRule | AvailabilityRule, dateStr: string): boolean => {
  const date = new Date(dateStr);
  const dayOfWeek = date.getDay(); // 0 = Sunday
  const dayOfMonth = date.getDate(); // 1-31
  
  // Check execution days based on processing frequency
  if (rule.executionDays && rule.executionDays.length > 0) {
    if (rule.processingFrequency === 'weekly') {
      // For weekly: executionDays contains day-of-week numbers (0-6)
      if (!rule.executionDays.includes(dayOfWeek)) return false;
    } else if (rule.processingFrequency === 'monthly') {
      // For monthly: executionDays contains day-of-month numbers (1-31)
      if (!rule.executionDays.includes(dayOfMonth)) return false;
    }
  }
  
  // Check day of week
  if (rule.daysOfWeek && rule.daysOfWeek.length > 0) {
    if (!rule.daysOfWeek.includes(dayOfWeek)) return false;
  }
  
  // Check date range
  if (rule.startDate) {
    const startDate = new Date(rule.startDate);
    startDate.setHours(0, 0, 0, 0);
    if (date < startDate) return false;
  }
  if (rule.endDate) {
    const endDate = new Date(rule.endDate);
    endDate.setHours(23, 59, 59, 999);
    if (date > endDate) return false;
  }
  
  return rule.isActive;
};

// Helper to check if an event is active on a given date
const isEventActiveOnDate = (event: Event, dateStr: string): boolean => {
  const date = new Date(dateStr);
  const startDate = new Date(event.startDate);
  const endDate = new Date(event.endDate);
  return date >= startDate && date <= endDate;
};

// Get active rules and events for a specific day
const getActiveRulesAndEvents = (
  dateStr: string,
  pricingRules: PricingRule[] = [],
  availabilityRules: AvailabilityRule[] = [],
  events: Event[] = []
) => {
  const activePricingRules = pricingRules.filter(rule => isRuleActiveOnDate(rule, dateStr));
  const activeAvailabilityRules = availabilityRules.filter(rule => isRuleActiveOnDate(rule, dateStr));
  const activeEvents = events.filter(event => isEventActiveOnDate(event, dateStr));
  
  return {
    pricingRules: activePricingRules,
    availabilityRules: activeAvailabilityRules,
    events: activeEvents
  };
};

export function CalendarView({ propertyId, basePrice, pricingRules = [], availabilityRules = [], events = [], property }: CalendarViewProps) {
  const { symbol } = useCurrencyFormat();
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [editedBasePrice, setEditedBasePrice] = useState<string>('');
  const [editedAdjustment, setEditedAdjustment] = useState<string>('');
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const updateCalendarDay = useUpdateCalendarDay(propertyId, year, month);
  
  // Generate base calendar structure
  const generateBaseCalendar = (): CalendarData => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    
    const days: CalendarDay[] = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      // Format date directly without timezone conversion
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isToday = date.toDateString() === today.toDateString();
      
      days.push({
        date: dateStr,
        price: basePrice,
        basePrice,
        percentChange: 0,
        occupancy: Math.round(60 + Math.random() * 35),
        type: 'standard',
        isToday,
      });
    }
    
    return {
      summary: {
        averagePrice: basePrice,
        averagePriceChange: 0,
        peakPrice: basePrice,
        peakPriceNote: 'Highest rate this month',
        lowestPrice: basePrice,
        lowestPriceNote: 'Best value days',
      },
      days,
    };
  };
  
  // Generate calendar data from available APIs
  const baseCalendar = generateBaseCalendar();
  const enrichedData = enrichCalendarWithRules(
    baseCalendar,
    basePrice,
    pricingRules,
    availabilityRules,
    events
  );
  // console.log("enrichedData", enrichedData);
  const calendarData = {
    success: true,
    data: enrichedData,
  };
  
  const isLoading = false;
  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
    setSelectedDay(null);
  };
  
  const goToToday = () => {
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDay(null);
  };
  
  const monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  
  // Calculate calendar grid
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  const calendarDays = calendarData?.data?.days || [];
  
  const getDayData = (day: number) => {
    // Format date directly without timezone conversion
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarDays.find(d => d.date === dateStr);
  };
  
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'high-demand':
        return 'bg-red-50 border-red-200';
      case 'discount':
        return 'bg-yellow-50 border-yellow-200';
      case 'event':
        return 'bg-purple-50 border-purple-200';
      case 'standard':
      default:
        return 'bg-green-50 border-green-200';
    }
  };
  
  const getPriceColor = (type: string) => {
    switch (type) {
      case 'high-demand':
        return 'text-red-600';
      case 'discount':
        return 'text-yellow-600';
      case 'event':
        return 'text-purple-600';
      case 'standard':
      default:
        return 'text-green-600';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'high-demand':
        return 'High Demand';
      case 'discount':
        return 'Discount Day';
      case 'event':
        return 'Event Day';
      case 'standard':
      default:
        return 'Standard';
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3 p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 !border-[#0f1729]"></div>
        </div>
      </div>
    );
  }
  
  const summary = calendarData?.data?.summary;
  
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 border-l-4 border-l-green-400">
          <p className="text-sm text-muted-foreground">Average Price</p>
          <p className="text-2xl font-bold font-heading mt-1">{symbol}{summary?.averagePrice || 0}</p>
          <div className="flex items-center gap-1 mt-1 text-sm text-green-600">
            <TrendingUp className="h-3.5 w-3.5" />
            <span>+{summary?.averagePriceChange || 0}% vs last month</span>
          </div>
        </Card>
        
        <Card className="p-5 border-l-4 border-l-blue-400">
          <p className="text-sm text-muted-foreground">Peak Price</p>
          <p className="text-2xl font-bold font-heading mt-1">{symbol}{summary?.peakPrice || 0}</p>
          <p className="text-sm text-muted-foreground mt-1">{summary?.peakPriceNote}</p>
        </Card>
        
        <Card className="p-5 border-l-4 border-l-amber-400">
          <p className="text-sm text-muted-foreground">Lowest Price</p>
          <p className="text-2xl font-bold font-heading mt-1">{symbol}{summary?.lowestPrice || 0}</p>
          <p className="text-sm text-muted-foreground mt-1">{summary?.lowestPriceNote}</p>
        </Card>
      </div>
      
      {/* Calendar and Detail Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className={cn("p-6", selectedDay ? "lg:col-span-2" : "lg:col-span-3")}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold font-heading">{monthYear}</h2>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => navigateMonth('prev')}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={goToToday}>Today</Button>
              <Button variant="outline" size="icon" onClick={() => navigateMonth('next')}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Weekday Headers */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {WEEKDAYS.map(day => (
              <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {/* Empty cells for days before first of month */}
            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square" />
            ))}
            
            {/* Day cells */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dayData = getDayData(day);
              const isToday = dayData?.isToday;
              const isSelected = selectedDay?.date === dayData?.date;
              
              return (
                <div
                  key={day}
                  onClick={() => {
                    if (dayData) {
                      // Format date directly without timezone conversion
                      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      console.log("Onclick", dateStr, dayData)
                      setSelectedDay({ ...dayData, date: dateStr });
                    }
                  }}
                  className={cn(
                    'min-h-[120px] p-2 rounded-lg border-2 flex flex-col justify-between transition-all cursor-pointer hover:shadow-md',
                    dayData ? getTypeColor(dayData.type) : 'bg-muted/30 border-border',
                    isToday && 'ring-2 ring-accent ring-offset-2',
                    isSelected && 'ring-2 ring-primary ring-offset-2 shadow-lg'
                  )}
                >
                  <div className="flex items-start justify-between">
                    <span className={cn(
                      'text-sm font-medium',
                      dayData?.type === 'high-demand' && 'text-red-700',
                      dayData?.type === 'discount' && 'text-yellow-700',
                      dayData?.type === 'event' && 'text-purple-700',
                      dayData?.type === 'standard' && 'text-green-700'
                    )}>
                      {day}
                    </span>
                  </div>
                  
                  {dayData && (() => {
                    // const dateStr = new Date(year, month, day).toISOString().split('T')[0];
                    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

                    const activeItems = getActiveRulesAndEvents(dateStr, pricingRules, availabilityRules, events);
                    
                    // Calculate total adjustment from pricing rules
                    const totalAdjustment = activeItems.pricingRules.reduce((sum, rule) => {
                      if (rule.adjustmentType === 'percentage') {
                        return sum + rule.adjustmentValue;
                      }
                      return sum;
                    }, 0);
                    
                    return (
                      <div className="flex-1 flex flex-col gap-1 mt-1 min-h-0">
                        {/* Event names */}
                        {activeItems.events.length > 0 && (
                          <div className="space-y-0.5">
                            {activeItems.events.slice(0, 2).map((event, idx) => (
                              <p key={idx} className="text-[10px] font-medium text-purple-700 truncate" title={event.name}>
                                📅 {event.name}
                              </p>
                            ))}
                            {activeItems.events.length > 2 && (
                              <p className="text-[9px] text-purple-600">+{activeItems.events.length - 2} more</p>
                            )}
                          </div>
                        )}
                        
                        {/* Adjustment value */}
                        {totalAdjustment !== 0 && (
                          <div className={cn(
                            'text-[11px] font-bold px-1.5 py-0.5 rounded',
                            totalAdjustment > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          )}>
                            {totalAdjustment > 0 ? '+' : ''}{totalAdjustment}%
                          </div>
                        )}
                        
                        {/* Rules count */}
                        {activeItems.pricingRules.length > 0 && (
                          <p className="text-[9px] text-blue-600">
                            {activeItems.pricingRules.length} rule{activeItems.pricingRules.length > 1 ? 's' : ''}
                          </p>
                        )}
                        
                        {/* Availability restrictions */}
                        {dayData.restrictions && dayData.restrictions.length > 0 && (
                          <div className="space-y-0.5">
                            {dayData.restrictions.slice(0, 2).map((restriction, idx) => (
                              <span key={idx} className="inline-block text-[9px] px-1 py-0.5 bg-orange-100 text-orange-700 rounded mr-1">
                                {restriction}
                              </span>
                            ))}
                            {dayData.restrictions.length > 2 && (
                              <span className="text-[8px] text-orange-600">+{dayData.restrictions.length - 2}</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  
                  {dayData && (
                    <div className="mt-auto pt-1 border-t border-current/10">
                      <p className={cn('text-sm font-bold', getPriceColor(dayData.type))}>
                        {symbol}{dayData.price}
                      </p>
                      <div className={cn(
                        'flex items-center gap-0.5 text-[10px]',
                        dayData.percentChange > 0 ? 'text-green-600' : dayData.percentChange < 0 ? 'text-red-600' : 'text-muted-foreground'
                      )}>
                        {dayData.percentChange > 0 ? (
                          <TrendingUp className="h-2.5 w-2.5" />
                        ) : dayData.percentChange < 0 ? (
                          <TrendingDown className="h-2.5 w-2.5" />
                        ) : null}
                        <span>{dayData.percentChange > 0 ? '+' : ''}{dayData.percentChange}%</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 mt-6 pt-4 border-t">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-100 border-2 border-green-200" />
              <span className="text-sm text-muted-foreground">Standard increase</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-100 border-2 border-red-200" />
              <span className="text-sm text-muted-foreground">High demand</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-100 border-2 border-yellow-200" />
              <span className="text-sm text-muted-foreground">Discount</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-purple-100 text-purple-700 border-purple-300">
                E
              </Badge>
              <span className="text-sm text-muted-foreground">Event</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700 border-blue-300">
                R
              </Badge>
              <span className="text-sm text-muted-foreground">Active rules</span>
            </div>
          </div>
        </Card>
        
        {/* Detail Panel */}
        {selectedDay && (
          <Card className="p-6 lg:col-span-1 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold font-heading">Day Details</h3>
              <Button variant="ghost" size="icon" onClick={() => setSelectedDay(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Date Header */}
            <div className="mb-6">
              <p className="text-2xl font-bold font-heading">
                {new Date(selectedDay.date).toLocaleDateString('en-US', { 
                  weekday: 'long',
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
              <Badge className={cn(
                'mt-2',
                selectedDay.type === 'high-demand' && 'bg-red-100 text-red-700 hover:bg-red-100',
                selectedDay.type === 'discount' && 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100',
                selectedDay.type === 'event' && 'bg-purple-100 text-purple-700 hover:bg-purple-100',
                selectedDay.type === 'standard' && 'bg-green-100 text-green-700 hover:bg-green-100'
              )}>
                {getTypeLabel(selectedDay.type)}
              </Badge>
              {(() => {
                const activeItems = getActiveRulesAndEvents(selectedDay.date, pricingRules, availabilityRules, events);
                return activeItems.events.length > 0 ? (
                  <div className="mt-2 space-y-1">
                    {activeItems.events.map((event) => (
                      <div key={event.id} className="space-y-1">
                        <button
                          onClick={() => setExpandedEventId(expandedEventId === event.id ? null : event.id)}
                          className="w-full text-left text-sm text-muted-foreground flex items-center gap-1 hover:text-foreground transition-colors"
                        >
                          <Calendar className="h-3.5 w-3.5" />
                          {event.name}
                          {expandedEventId === event.id ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
                        </button>
                        {expandedEventId === event.id && (
                          <div className="ml-5 text-xs space-y-1 text-muted-foreground animate-fade-in">
                            <p><span className="font-medium">Category:</span> {event.category}</p>
                            <p><span className="font-medium">Impact:</span> {event.impact}</p>
                            <p><span className="font-medium">Multiplier:</span> {event.pricingMultiplier}x</p>
                            <p><span className="font-medium">Dates:</span> {new Date(event.startDate).toLocaleDateString()} - {new Date(event.endDate).toLocaleDateString()}</p>
                            {event.description && <p><span className="font-medium">Description:</span> {event.description}</p>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}
            </div>
            
            {/* Pricing Breakdown */}
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Pricing Breakdown
                  </h4>
                  {/* {!isEditingPrice ? (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-7 text-xs gap-1"
                      onClick={() => {
                        setIsEditingPrice(true);
                        setEditedBasePrice(selectedDay.basePrice.toString());
                        setEditedAdjustment(selectedDay.percentChange.toString());
                      }}
                    >
                      <Pencil className="h-3 w-3" />
                      Edit
                    </Button>
                  ) : (
                    <Button 
                      variant="default" 
                      size="sm" 
                      className="h-7 text-xs gap-1"
                      disabled={updateCalendarDay.isPending}
                      onClick={() => {
                        const newBase = parseFloat(editedBasePrice);
                        const newAdj = parseFloat(editedAdjustment);
                        if (isNaN(newBase) || newBase <= 0) {
                          toast.error('Please enter a valid base price');
                          return;
                        }
                        if (isNaN(newAdj)) {
                          toast.error('Please enter a valid adjustment percentage');
                          return;
                        }
                        
                        updateCalendarDay.mutate({
                          propertyId,
                          date: selectedDay.date,
                          basePrice: newBase,
                          percentChange: newAdj,
                        }, {
                          onSuccess: (response) => {
                            if (response.success && response.data) {
                              setSelectedDay(response.data);
                              toast.success(`Price updated to ${symbol}${response.data.price}`);
                            } else {
                              toast.error(response.error || 'Failed to update price');
                            }
                            setIsEditingPrice(false);
                          },
                          onError: () => {
                            toast.error('Failed to update price');
                          }
                        });
                      }}
                    >
                      {updateCalendarDay.isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Check className="h-3 w-3" />
                      )}
                      Save
                    </Button>
                  )} */}
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-muted-foreground">Base Price</span>
                    {isEditingPrice ? (
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground">{symbol}</span>
                        <Input
                          type="number"
                          value={editedBasePrice}
                          onChange={(e) => setEditedBasePrice(e.target.value)}
                          className="w-20 h-7 text-right font-mono"
                        />
                      </div>
                    ) : (
                      <span className="font-mono font-medium">{symbol}{selectedDay.basePrice}</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-muted-foreground">Adjustment</span>
                    {isEditingPrice ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={editedAdjustment}
                          onChange={(e) => setEditedAdjustment(e.target.value)}
                          className="w-20 h-7 text-right font-mono"
                        />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    ) : (
                      <span className={cn(
                        'font-mono font-medium',
                        selectedDay.percentChange > 0 ? 'text-green-600' : selectedDay.percentChange < 0 ? 'text-red-600' : ''
                      )}>
                        {selectedDay.percentChange > 0 ? '+' : ''}{selectedDay.percentChange}%
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between items-center py-2 bg-muted/50 rounded-lg px-3 -mx-3">
                    <span className="font-semibold">Final Price</span>
                    <span className="text-xl font-bold font-heading text-primary">
                      {symbol}{isEditingPrice 
                        ? Math.round((parseFloat(editedBasePrice) || 0) * (1 + (parseFloat(editedAdjustment) || 0) / 100))
                        : selectedDay.price
                      }
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Occupancy */}
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Occupancy
                </h4>
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-end gap-2 mb-2">
                    <span className="text-3xl font-bold font-heading">{selectedDay.occupancy}%</span>
                    <span className="text-sm text-muted-foreground mb-1">occupied</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className={cn(
                        'h-2 rounded-full transition-all',
                        selectedDay.occupancy >= 80 ? 'bg-green-500' : 
                        selectedDay.occupancy >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                      )}
                      style={{ width: `${selectedDay.occupancy}%` }}
                    />
                  </div>
                </div>
              </div>
              
              {/* Active Rules */}
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  Active Rules
                </h4>
                <div className="space-y-2">
                  {(() => {
                    const activeItems = getActiveRulesAndEvents(selectedDay.date, pricingRules, availabilityRules, events);
                    const allRules = [
                      ...activeItems.pricingRules.map(r => ({ ...r, ruleType: 'pricing' as const })),
                      ...activeItems.availabilityRules.map(r => ({ ...r, ruleType: 'availability' as const }))
                    ];
                    
                    if (allRules.length === 0) {
                      return (
                        <div className="text-center py-4 text-sm text-muted-foreground">
                          No active rules for this day
                        </div>
                      );
                    }
                    
                    return allRules.map((rule) => {
                      const isPricing = rule.ruleType === 'pricing';
                      const adjustment = isPricing && 'adjustmentValue' in rule 
                        ? `${rule.adjustmentValue >= 0 ? '+' : ''}${rule.adjustmentValue}${rule.adjustmentType === 'percentage' ? '%' : symbol}`
                        : '';
                      const isExpanded = expandedRuleId === rule.id;
                      
                      return (
                        <div key={rule.id} className="space-y-2">
                          <button
                            onClick={() => setExpandedRuleId(isExpanded ? null : rule.id)}
                            className="w-full flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors text-left"
                          >
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={cn(
                                'text-[10px]',
                                isPricing ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-orange-50 text-orange-700 border-orange-200'
                              )}>
                                {rule.ruleType}
                              </Badge>
                              <span className="text-sm font-medium">{rule.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {adjustment && (
                                <span className={cn(
                                  'text-sm font-mono font-medium',
                                  adjustment.startsWith('+') ? 'text-green-600' : 'text-red-600'
                                )}>
                                  {adjustment}
                                </span>
                              )}
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </div>
                          </button>
                          {isExpanded && (
                            <div className="ml-3 p-3 space-y-2 text-xs bg-muted/30 rounded-lg animate-fade-in">
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <span className="font-medium text-muted-foreground">Priority:</span>
                                  <p className="text-foreground">{rule.priority}</p>
                                </div>
                                <div>
                                  <span className="font-medium text-muted-foreground">Status:</span>
                                  <p className="text-foreground">{rule.isActive ? 'Active' : 'Inactive'}</p>
                                </div>
                              </div>
                              <div>
                                <span className="font-medium text-muted-foreground">Date Range:</span>
                                <p className="text-foreground">
                                  {new Date(rule.startDate).toLocaleDateString()}
                                  {rule.endDate && ` - ${new Date(rule.endDate).toLocaleDateString()}`}
                                </p>
                              </div>
                              {rule.daysOfWeek && rule.daysOfWeek.length > 0 && (
                                <div>
                                  <span className="font-medium text-muted-foreground">Days:</span>
                                  <p className="text-foreground">
                                    {rule.daysOfWeek.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')}
                                  </p>
                                </div>
                              )}
                              {isPricing && 'applyTo' in rule && (
                                <div>
                                  <span className="font-medium text-muted-foreground">Apply To:</span>
                                  <p className="text-foreground">{rule.applyTo === 'basePrice' ? 'Base Price' : 'Current Price'}</p>
                                </div>
                              )}
                              {!isPricing && 'actionType' in rule && (
                                <div>
                                  <span className="font-medium text-muted-foreground">Action:</span>
                                  <p className="text-foreground">{rule.actionType}: {rule.actionValue}</p>
                                </div>
                              )}
                              {rule.roomTypes && rule.roomTypes.length > 0 && (
                                <div>
                                  <span className="font-medium text-muted-foreground">Room Types:</span>
                                  <p className="text-foreground">{rule.roomTypes.length} type{rule.roomTypes.length > 1 ? 's' : ''} affected</p>
                                </div>
                              )}
                              {rule.otaChannels && rule.otaChannels.length > 0 && (
                                <div>
                                  <span className="font-medium text-muted-foreground">OTA Channels:</span>
                                  <p className="text-foreground">{rule.otaChannels.length} channel{rule.otaChannels.length > 1 ? 's' : ''} affected</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  })()}
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
